import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, Loader2 } from 'lucide-react';
import { ADMIN_TOKEN_KEY } from '../pages/AdminPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Header-mounted quick action for the admin — one click to blast fresh
 * releases to every subscriber. Only mounts when a valid ADMIN_TOKEN is
 * present in localStorage. If the token is invalid the backend returns
 * 401 and the button self-hides.
 */
export default function AdminQuickBlast() {
  const [adminToken, setAdminToken] = useState(
    () => localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  );
  const [busy, setBusy] = useState(false);

  // Keep in sync if the admin signs in/out in another tab
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === ADMIN_TOKEN_KEY) setAdminToken(e.newValue || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!adminToken) return null;

  const blast = async () => {
    const confirmed = window.confirm(
      'Send the fresh-drop email to ALL newsletter subscribers?\n\n' +
      'This will alert them about every new release currently in the ' +
      '±7 day window. Idempotent — already-alerted titles are skipped.'
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/digest/new-releases`,
        {},
        { headers: { 'X-Admin-Token': adminToken } }
      );
      const r = res.data.result;
      if (!r.new_count) {
        toast.info(
          'No un-alerted new releases right now. Use /admin → "Force resend all" to re-blast.'
        );
      } else {
        toast.success(
          `Blast sent — ${r.new_count} title(s) to ${r.sent}/${r.recipients} subscriber(s).`
        );
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        // Wipe the bad token — the button will unmount on next render.
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAdminToken('');
        toast.error('Admin token invalid. Sign in again at /admin.');
      } else {
        const detail = e?.response?.data?.detail || e.message;
        toast.error(`Blast failed: ${detail}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={blast}
      disabled={busy}
      data-testid="admin-quick-blast-btn"
      title="Blast new releases to all subscribers"
      className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-xs uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-emerald-400"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Zap className="w-4 h-4" />
      )}
      <span>{busy ? 'Blasting…' : 'Blast New Releases'}</span>
    </button>
  );
}
